import { useCallback } from 'react'
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormProps,
  UseFormReturn,
  useForm as useReactHookForm,
} from 'react-hook-form'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import { HcFetchError } from '@/apiClient'

export const useSmartForm = <
  TFieldValues extends FieldValues = FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props?: UseFormProps<TFieldValues, TContext>
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const form = useReactHookForm<TFieldValues, TContext, TTransformedValues>(
    props
  )

  // Preserve the original handleSubmit function
  const originalHandleSubmit = form.handleSubmit

  // Create a type-safe wrapper around handleSubmit
  const smartHandleSubmit = useCallback(
    <TSubmitFieldValues extends FieldValues>(
      onValid: SubmitHandler<TSubmitFieldValues>,
      onInvalid?: SubmitErrorHandler<TSubmitFieldValues>
    ) => {
      // Create a wrapper around the onValid function that catches Zod errors
      const handleValidWithErrorCatching: SubmitHandler<
        TSubmitFieldValues
      > = async (data, event) => {
        try {
          return await onValid(data, event)
        } catch (error: unknown) {
          if (error instanceof HcFetchError) {
            const response = error.data
            const zodResponse = response as {
              success: false
              error: ZodError | { cause: ZodError['issues'] }
            }
            const errorPayload = zodResponse.error
            if (
              ('name' in errorPayload && errorPayload.name === 'ZodError') ||
              ('code' in errorPayload &&
                errorPayload.code === 'VALIDATION_ERROR')
            ) {
              const issues =
                'name' in errorPayload
                  ? errorPayload.issues
                  : errorPayload.cause
              issues.forEach((issue) => {
                const shouldShorten =
                  issue.code === 'invalid_enum_value' &&
                  issue.options.length > 5
                const message = shouldShorten
                  ? issue.message.split('.')?.[0]
                  : issue.message
                // @ts-expect-error dynamic string type
                form.setError(issue.path.join('.'), {
                  type: issue.code,
                  message,
                })
              })

              const errorCount = issues.length

              toast.error(
                `Il form contiene ${errorCount === 1 ? 'un' : errorCount} ${errorCount > 1 ? 'errori' : 'errore'}`,
                { important: true }
              )

              return
            }
          }

          toast.error('Si è verificato un errore', { important: true })

          throw error
        }
      }

      // Use the original handleSubmit with our error-catching wrapper
      // @ts-expect-error Type compatibility between generic form values
      return originalHandleSubmit(handleValidWithErrorCatching, onInvalid)
    },
    [form, originalHandleSubmit]
  )

  form.handleSubmit = smartHandleSubmit as typeof form.handleSubmit

  return form
}
